<template>
  <RouterLink v-if="to"
    :to="to"
    data-turbo-prefetch="false"
    :aria-current="active ? 'page' : undefined"
    class="relative flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 text-[13px] font-medium"
    :class="[linkClass, $attrs.class, collapsed ? 'justify-center' : '']"
    v-bind="attrsWithoutClass"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <span class="flex-shrink-0 w-[18px] h-[18px]"><slot name="icon" /></span>
    <slot />
  </RouterLink>

  <a v-else
    :href="href"
    data-turbo="false"
    :aria-current="active ? 'page' : undefined"
    class="relative flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150 text-[13px] font-medium"
    :class="[linkClass, $attrs.class, collapsed ? 'justify-center' : '']"
    v-bind="attrsWithoutClass"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <span class="flex-shrink-0 w-[18px] h-[18px]"><slot name="icon" /></span>
    <slot />
  </a>

  <Teleport to="body">
    <Transition name="sb-flyout">
      <component
        v-if="showFlyout && collapsed && label"
        :is="to ? RouterLink : 'a'"
        :to="to || undefined"
        :href="!to ? href : undefined"
        :data-turbo-prefetch="to ? 'false' : undefined"
        :data-turbo="!to ? 'false' : undefined"
        class="sb-flyout fixed z-[9999] flex items-center px-3.5 py-2 rounded-lg text-[13px] font-semibold text-white whitespace-nowrap border border-white/15 shadow-xl shadow-black/40 cursor-pointer transition-colors duration-100"
        :class="active ? 'bg-white/20' : 'bg-blue-950 hover:bg-blue-900'"
        :style="flyoutStyle"
        @mouseenter="onFlyoutEnter"
        @mouseleave="onFlyoutLeave"
        @click="showFlyout = false"
      >
        {{ label }}
      </component>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, inject, useAttrs } from 'vue'
import { RouterLink } from 'vue-router'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  href: { type: String, default: null },
  to: { type: String, default: null },
  active: { type: Boolean, default: false },
  label: { type: String, default: null },
})

const collapsed = inject('sidebarCollapsed', ref(false))

const attrs = useAttrs()

const attrsWithoutClass = computed(() => {
  const { class: _, ...rest } = attrs
  return rest
})

const linkClass = computed(() =>
  props.active
    ? 'bg-white/[0.18] text-white'
    : 'text-white/60 hover:bg-white/[0.08] hover:text-white'
)

const showFlyout = ref(false)
const flyoutStyle = ref({})
let hideTimer = null

function onMouseEnter(e) {
  clearTimeout(hideTimer)
  if (!collapsed.value || !props.label) return
  const rect = e.currentTarget.getBoundingClientRect()
  flyoutStyle.value = {
    top: `${rect.top + rect.height / 2}px`,
    left: `${rect.right + 10}px`,
  }
  showFlyout.value = true
}

function onMouseLeave() {
  hideTimer = setTimeout(() => { showFlyout.value = false }, 80)
}

function onFlyoutEnter() {
  clearTimeout(hideTimer)
}

function onFlyoutLeave() {
  showFlyout.value = false
}
</script>

<style scoped>
.sb-flyout {
  transform: translateY(-50%);
}

.sb-flyout::before {
  content: '';
  position: absolute;
  left: -5px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-right: 5px solid #172554;
}

.sb-flyout-enter-active,
.sb-flyout-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.sb-flyout-enter-from,
.sb-flyout-leave-to {
  opacity: 0;
  transform: translateX(-6px) translateY(-50%);
}

.sb-flyout-enter-to,
.sb-flyout-leave-from {
  opacity: 1;
  transform: translateX(0) translateY(-50%);
}
</style>
